import axios, { AxiosError } from 'axios'
import React from 'react'
import { IconTimes } from '../../icons/library'
import { showAxiosErrorAsToast, makeRequest } from '../../request'
import { Toasts } from '../../providers/toast'
import { Button, ButtonStyle, ButtonShape } from '../simple/button'
import { Tooltip } from '../simple/tooltip'

const DEFAULT_TOOLTIP =
  'This document can be auto-translated, but you cannot rely on automatic translation for legal documents!'

type UploadingFile = { file: File; uuid: string; progress: number }
type FailedFile = { fileName: string; uuid: string; error: string }
export type UploadedFile = { fileName: string; uuid: string; tooltip?: string }

export interface FileUploadRowHandle {
  upload: (files: File[]) => void
}
interface FileUploadRowProps {
  /** The maximum number of files that can be uploaded */
  maxFiles?: number
  /** Limits the allowed file types for upload, if provided */
  allowedFileTypes?: string[]
  /** The button to trigger the file upload */
  children: React.ReactElement
  /** The current uploaded files */
  uploadedFiles: UploadedFile[]
  /** Callback function to set the uploaded files */
  setUploadedFiles: React.Dispatch<React.SetStateAction<UploadedFile[]>>
  /** Callback function to indicate if files are currently being uploaded */
  onUploading?: (uploading: boolean) => void
  /** If true, outline the in-progress uploads to make sure the user notices them */
  highlightInProgressUploads?: boolean
}

const fileKey = (file: File) => `${file.name}-${Math.random()}`

/**
 * FileUploadRow component is a file upload button that allows the user to select and upload files.
 * Uploaded files are displayed with their names and progress, after the button in the row.
 */
export const FileUploadRow = React.forwardRef<FileUploadRowHandle, FileUploadRowProps>(
  (
    {
      children,
      maxFiles = 99,
      allowedFileTypes,
      uploadedFiles,
      setUploadedFiles,
      highlightInProgressUploads,
      onUploading = () => {},
    },
    ref
  ) => {
    const [uploadingFiles, setUploadingFiles] = React.useState<UploadingFile[]>([])
    const [failedFiles, setFailedFiles] = React.useState<FailedFile[]>([])
    const inputRef = React.useRef<HTMLInputElement>(null)

    React.useImperativeHandle(ref, () => ({
      upload: onFilesUploaded,
    }))

    const onFilesUploaded = (newFiles: File[]) => {
      const fileCount = uploadingFiles.length + uploadedFiles.length
      if (newFiles.length > maxFiles - fileCount) {
        Toasts.showToast({ message: `You can only upload ${maxFiles} files`, error: true })
        newFiles.splice(maxFiles - fileCount)
      }
      if (allowedFileTypes && newFiles.some((file) => !allowedFileTypes.includes(file.type))) {
        Toasts.showToast({ message: 'Invalid file type', error: true })
        newFiles = newFiles.filter((file) => allowedFileTypes.includes(file.type))
      }
      if (newFiles.length === 0) return
      const newWrappedFiles: UploadingFile[] = newFiles.map((file) => ({ file, uuid: fileKey(file), progress: 0 }))
      setUploadingFiles((files) => [...files, ...newWrappedFiles])
      onUploading(true)
      for (const newWrappedFile of newWrappedFiles) {
        uploadFile(newWrappedFile.file, (progress) => {
          setUploadingFiles((files) =>
            files.map((file) => (file.uuid === newWrappedFile.uuid ? { ...file, progress } : file))
          )
        })
          .then((warning) => {
            onUploading(uploadingFiles.length > 1)
            setUploadingFiles((files) => files.filter((file) => file.uuid !== newWrappedFile.uuid))

            const tooltip = warning || DEFAULT_TOOLTIP

            setUploadedFiles((files) => [
              ...files,
              { uuid: newWrappedFile.uuid, fileName: newWrappedFile.file.name, tooltip },
            ])
          })
          .catch((error) => {
            showAxiosErrorAsToast(error)
            onUploading(uploadingFiles.length > 1)
            setUploadingFiles((files) => files.filter((file) => file.uuid !== newWrappedFile.uuid))
            setFailedFiles((files) => [
              ...files,
              { uuid: newWrappedFile.uuid, fileName: newWrappedFile.file.name, error: error.message, status: 'failed' },
            ])
          })
      }
    }

    const childrenWithOnClick = React.cloneElement(children, {
      onClick: () => {
        if (inputRef.current) inputRef.current.click()
      },
    })

    return (
      <div className="flex min-w-0 flex-row flex-wrap gap-2 overflow-auto">
        <input
          type="file"
          hidden
          multiple={maxFiles > 1}
          accept={allowedFileTypes ? allowedFileTypes.join(',') : undefined}
          ref={inputRef}
          onChange={(event) => {
            if (event.target.files) onFilesUploaded(Array.from(event.target.files))
          }}
        />
        {childrenWithOnClick}
        {uploadingFiles.map((file) => (
          <FileElement
            name={file.file.name}
            key={file.uuid}
            progress={file.progress}
            outline={highlightInProgressUploads}
          />
        ))}
        {uploadedFiles.map((file) => (
          <FileElement
            name={file.fileName}
            key={file.uuid}
            tooltip={file.tooltip}
            onDelete={() => {
              setUploadedFiles((files) => files.filter((f) => f.uuid !== file.uuid))
            }}
          />
        ))}
        {failedFiles.map((file) => (
          <FileElement
            name={file.fileName}
            key={file.uuid}
            tooltip={file.error}
            onDelete={() => {
              setFailedFiles((files) => files.filter((f) => f.uuid !== file.uuid))
            }}
            failed
          />
        ))}
      </div>
    )
  }
)

interface FileElementProps {
  name: string
  onDelete?: () => void
  progress?: number
  tooltip?: string
  failed?: boolean
  outline?: boolean
}

const FileElement: React.FC<FileElementProps> = ({ name, onDelete, progress, tooltip, failed, outline }) => {
  const warning = tooltip && tooltip !== DEFAULT_TOOLTIP
  const outlineClass = outline ? 'border border-red-500' : ''
  const fileElem = (
    <div
      className={`flex max-h-[32px] min-h-[32px] max-w-96 flex-row items-center gap-2 rounded-full px-3 py-1 ${outlineClass} ${failed ? 'bg-red-500 text-white' : warning ? 'bg-amber-100' : 'bg-slate-100'}`}
    >
      <div className="overflow-hidden text-ellipsis text-nowrap">{name}</div>
      {progress !== undefined && (
        <div className="relative h-2 w-24 flex-shrink-0 rounded-full bg-slate-300">
          <div
            className="absolute left-0 h-full rounded-full bg-blue-500 transition-all duration-300"
            style={{ width: `${progress * 100}%` }}
          />
        </div>
      )}
      {onDelete && (
        <Button
          buttonStyle={ButtonStyle.toastClose}
          shape={ButtonShape.close}
          className={`-mr-2 flex-shrink-0 ${failed ? '!text-white' : ''}`}
          onClick={onDelete}
        >
          <IconTimes thicken={20} />
        </Button>
      )}
    </div>
  )

  return tooltip ? <Tooltip text={tooltip}>{fileElem}</Tooltip> : fileElem
}

async function uploadFile(file: File, setProgress: (progress: number) => void): Promise<string | undefined> {
  const url = await getPresignedUrl(file.name)
  await uploadToS3(url, file, setProgress)
  return await validateFile(file.name)
}

export async function getPresignedUrl(name: string): Promise<string> {
  try {
    return (await makeRequest<{ url: string }>('/presignedURL', { filename: name })).url
  } catch (error) {
    throw new AxiosError('Failed to get presigned URL for file upload')
  }
}

export async function uploadToS3(
  url: string,
  file: File,
  setProgress: (progress: number) => void = () => {}
): Promise<void> {
  try {
    await axios({
      url,
      data: file,
      method: 'PUT',
      onUploadProgress: (event) => {
        if (event.lengthComputable && event.total) setProgress(event.loaded / event.total)
      },
      headers: { 'Content-Type': file.type }, // TODO is this line needed still
    })
  } catch (error) {
    throw new AxiosError('Failed to upload file to S3')
  }
}

async function validateFile(name: string): Promise<string | undefined> {
  let warning
  try {
    warning = await makeRequest<string | undefined>('/verifyFile', { name })
  } catch (error) {
    throw new AxiosError('Failed to verify file')
  }
  if (warning === 'invalid extension') throw new AxiosError('File type not allowed')
  /**
   * This is the old behavior with lexikeet warning, we arent bothering with this at the moment because its really difficult to do well. maybe warn on send?
   * if (warning == 'lexikeet' && $('.student-recipient.selected').length == 1 && $('#to-selector .btn.selected').length == 1 && $('.class-recipient.selected').length == 0 && globe.length && !globe.data('tippy-content').includes('and speaks')) {
          warning = window.tr('You cannot use automatic translation for long documents to one person. If this is what you need, contact lexikeet for professional translation services.');
          tippy(root.find('span[data-name="' + file.name + '"]')[0], { content: warning });
          root.find('span[data-name="' + file.name + '"]').addClass('lexikeet');
      }
   */
  if (warning === 'lexikeet') return DEFAULT_TOOLTIP
  return warning
}

export async function inputFiles(accept: string | undefined, multiple: boolean = true): Promise<FileList | undefined> {
  return new Promise((resolve) => {
    const input = document.createElement('input')
    input.type = 'file'
    input.accept = accept || ''
    input.multiple = multiple
    input.onchange = () => resolve(input.files ?? undefined)
    input.oncancel = () => resolve(undefined)
    input.click()
  })
}

export async function inputSingleFile(accept?: string): Promise<File | undefined> {
  const files = await inputFiles(accept, false)
  return files ? files[0] : undefined
}
